import * as yup from "yup";
import {
    Alert,
    Box,
    Button,
    Card,
    Container,
    Divider,
    Typography,
    useTheme,
} from "@mui/material";
import { useRef, useState } from "react";
import { Formik, FormikProps } from "formik";
import PaTextField from "components/field/PaTextField";
import GoogleLoginIconButton from "./thirdParties/GoogleLoginIconButton";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import CustomSpinner from "components/display/CustomSpinner";
import { toast } from 'react-toastify';
import { createApi } from "util/ApiCreator";
import { AuthController } from "api/main/AuthController";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import { useNavigate } from "react-router-dom";
import CommonConstants from "constants/CommonConstants";

export default function LoginPage() {
    const theme = useTheme();
    const navigate = useNavigate();
    const authOldAPI = useRef(createApi("authold") as AuthController);
    const [loading, setLoading] = useState<boolean>(false);
    const [letterSent, setLetterSent] = useState<boolean>(false);
    const [error, setError] = useState<string>();

    const notify = () => toast.success('Prisijungimo nuoroda išsiųsta elektroniniu paštu', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
    });

    async function login(values: any) {
        if (loading) return;

        setLoading(true);
        try {
            await authOldAPI.current.sendMagicLink({
                email: values.email.toLowerCase(),
            });
            setLetterSent(true);
            notify();
        } catch (e: any) {
            const message = e?.response?.data ?? "Klaida, bandykite dar kartą.";
            setError(message);
        }
        setLoading(false);
    }

    function getButtonLabel() {
        if (letterSent) {
            return "Siųsti prisijungimo nuorodą dar kartą";
        }
        return "Gauti prisijungimo nuorodą";
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Header */}
            <Box
                component="header"
                sx={{
                    bgcolor: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(20px)",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
                    py: 2,
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
                            onClick={() => navigate(CommonConstants.loggedOffHomepage)}
                        >
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2,
                                    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <AutoGraphIcon sx={{ color: "white", fontSize: 24 }} />
                            </Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                }}
                            >
                                recodinAI
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Main Content */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 4,
                }}
            >
                <Container maxWidth="sm">
                    <Card
                        sx={{
                            p: { xs: 3, sm: 5 },
                            borderRadius: 4,
                            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.1)",
                            border: "1px solid rgba(99, 102, 241, 0.1)",
                        }}
                    >
                        <Box sx={{ textAlign: "center", mb: 4 }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: "#0F172A",
                                    mb: 1,
                                }}
                            >
                                Prisijungimas
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: "#64748B" }}
                            >
                                Įveskite savo el. paštą ir gaukite prisijungimo nuorodą
                            </Typography>
                        </Box>

                        {letterSent && (
                            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                                Prisijungimo nuoroda išsiųsta! Patikrinkite savo el. paštą.
                            </Alert>
                        )}

                        <Formik
                            initialValues={getInitialValues()}
                            validationSchema={getSchemaValidation()}
                            onSubmit={(values: any) => login(values)}
                        >
                            {(formik: FormikProps<any>) => (
                                <Box>
                                    {error && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                mb: 2,
                                                p: 2,
                                                bgcolor: "rgba(239, 68, 68, 0.1)",
                                                borderRadius: 2,
                                            }}
                                        >
                                            <ErrorRoundedIcon sx={{ color: theme.palette.error.main }} />
                                            <Typography
                                                variant="body2"
                                                color={theme.palette.error.main}
                                            >
                                                {error}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Box sx={{ mb: 3 }}>
                                        <PaTextField
                                            label={"El. paštas"}
                                            name={"email"}
                                            type={"email"}
                                            autoFocus
                                            fullWidth
                                            hideError
                                        />
                                    </Box>

                                    <Button
                                        variant="contained"
                                        size="large"
                                        type="submit"
                                        onClick={formik.submitForm}
                                        fullWidth
                                        sx={{
                                            background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                                            py: 1.5,
                                            fontSize: "1rem",
                                            "&:hover": {
                                                background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                                            },
                                        }}
                                    >
                                        {loading && (
                                            <CustomSpinner
                                                label={""}
                                                size={"small"}
                                                color={"white"}
                                                noPadding
                                            />
                                        )}
                                        {!loading && getButtonLabel()}
                                    </Button>
                                </Box>
                            )}
                        </Formik>

                        <Divider sx={{ my: 3 }}>
                            <Typography variant="body2" sx={{ color: "#94A3B8" }}>
                                arba
                            </Typography>
                        </Divider>

                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <GoogleLoginIconButton />
                        </Box>

                        <Box sx={{ textAlign: "center", mt: 4 }}>
                            <Typography variant="body2" sx={{ color: "#94A3B8" }}>
                                Neturite paskyros?{" "}
                                <Box
                                    component="span"
                                    sx={{
                                        color: "#6366F1",
                                        fontWeight: 600,
                                        cursor: "pointer",
                                        "&:hover": { textDecoration: "underline" },
                                    }}
                                    onClick={() => navigate(CommonConstants.loggedOffHomepage)}
                                >
                                    Susisiekite su mumis
                                </Box>
                            </Typography>
                        </Box>
                    </Card>
                </Container>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    py: 3,
                    borderTop: "1px solid #E2E8F0",
                    bgcolor: "white",
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 2,
                        }}
                    >
                        <Typography variant="body2" sx={{ color: "#64748B" }}>
                            © {new Date().getFullYear()} recodinAI. Visos teisės saugomos.
                        </Typography>
                        <Box
                            component="a"
                            href={process.env.PUBLIC_URL + "/privatumo_pranesimas.pdf"}
                            target="_blank"
                            sx={{
                                color: "#6366F1",
                                textDecoration: "none",
                                fontSize: "0.875rem",
                                "&:hover": { textDecoration: "underline" },
                            }}
                        >
                            Privatumo pranešimas
                        </Box>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}

function getSchemaValidation() {
    return yup.object({
        email: yup.string().email("El. paštas yra neteisingo formato"),
    });
}

function getInitialValues() {
    return { email: "" };
}
